import { FieldHelperTextManager } from "kz-ui-sdk";
import * as React from "react";
import { cloneElement, isValidElement } from "react";
import type { Control, Path } from "react-hook-form";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface RHFControllerProps<T extends object> extends React.PropsWithChildren {
  name: keyof T;
  control: Control<T>;
  onChange?: (...event: any[]) => void;
  helperText?: string;
  helperTextParams?: Record<string, string | number | undefined>;
  onBlur?: (value: any) => void;
  excludedValues?: any[];
  hideErrorText?: boolean;
  fieldHelperTextManager?: FieldHelperTextManager;
}

export default function RHFController<T extends object>({
  name,
  control,
  children,
  onChange,
  helperText,
  helperTextParams,
  onBlur,
  excludedValues = [],
  hideErrorText = false,
  fieldHelperTextManager,
}: RHFControllerProps<T>) {
  const { t } = useTranslation();

  return (
    <Controller
      control={control}
      name={name as unknown as Path<T>}
      render={({ field, fieldState: { error } }) => {
        const { ref: _ref, ...rest } = field;

        const shouldShowError = !!error && (!fieldHelperTextManager || (fieldHelperTextManager.submitCount ?? 0) > 0);

        return isValidElement(children) ? (
          cloneElement(children, {
            ...rest,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            disabled: (rest?.disabled ?? false) || ((children.props?.disabled ?? false) as boolean),
            error: shouldShowError,
            helperText: t(helperText ? helperText : hideErrorText ? "" : (error?.message as string), helperTextParams),
            onChange: (v: any) => {
              if (excludedValues?.includes(v)) return;
              rest.onChange(v);
              onChange?.(v);
            },
            onFocus: () => {
              fieldHelperTextManager?.triggerError(String(name));
              if (children.props?.onFocus) {
                children.props.onFocus();
              }
            },
            onBlur: () => {
              rest.onBlur();
              onBlur?.(rest.value);
            },
            checked: typeof rest.value === "boolean" ? rest.value : undefined,
            fieldHelperTextManager,
            submitCount: fieldHelperTextManager?.submitCount,
          } as typeof rest)
        ) : (
          <>{children}</>
        );
      }}
    />
  );
}
